import React from 'react';
import PropTypes from 'prop-types';

import Body from '../Components/layout/Body';
import { TABS } from './ConstPaths';

const DO_EVAL_HOME_HELP = `
<div>
  <h5>What can I expect when using Access Navigator?</h5>
  <p>Access Navigator provides a structure that helps you and your team make good decisions. It is not a magic wand that "does it for you." It can't (and shouldn't) take the place of your skills, but it provides support throughout, so you can be more efficient and effective.</p>
  <p>We've tried to design the app to:</p>
  <ul>
    <li>Make access assessments more fun for you and the client</li> 
    <li>Take the high-stakes worry out of access assessments</li>
    <li>Walk you through the steps in a logical sequence</li>
    <li>But let you skip around if needed or desired</li>
  </ul>
</div>
<div>
  <h5>What assessment process does Access Navigator use?</h5>
  <p>The app supports the assessment and service delivery process commonly used within assistive technology. This includes:</p> 
  <ul>
    <li>Gather information and identify goals</li>
    <li>Identify tools and strategies that might meet those goals</li>
    <li>Test-drive one or more of those tools</li>
    <li>Compare performance and satisfaction across test-drives</li>
    <li>Determine whether a solution has been found. (If not, continue seeking.)</li>
    <li>Implement the solution</li>
    <li>Follow-up and follow-along to ensure continued success</li>
  </ul>
  <p><a target="_blank" href="/docs/Access-Navigator-assessment-approach.pdf">Open our approach doc</a> to read a more detailed description of this process (opens in a new tab). 
  Our approach follows AT service delivery models such as <a href="https://atohio.org/sites/default/files/uploads/intro_to_at_assessment_models.pdf" target="_blank">the HAAT model and SETT framework</a>. 
  </p>
</div>
<div>
  <h5>How might Access Navigator help us?</h5>
  <ul>
    <li>Steps you through the process</li>
    <li>Helps you prepare for the assessment</li>
    <li>Helps you evaluate client abilities (including motor, perceptual, and cognitive)</li>
    <li>Provides ideas on what access options to choose from</li>
    <li>Allows you and your client to try out and compare options to make informed decisions</li>
    <li>Provides support for remedies to enhance access</li>
    <li>Helps you structure recommendations</li>
    <li>Content developed by experienced therapists</li>
  </ul>
</div>
<div>
  <h5>Does Access Navigator include the client in the evaluation process? Yes!</h5>
  <ul>
    <li>You and the client run through Access Navigator together</li>
    <li>Client's input is included throughout, including goals, wants, needs, as well as ratings and feedback after each trial</li>
  </ul>
</div>
<div>
  <h5>The importance of appropriate seating / positioning</h5>
  <p>Seating and positioning is a key foundation for successful alternative access.  Please consider the following:</p>
  <ul>
    <li>For many individuals, successful access depends on appropriate seating and positioning.  Without a strong postural base of support, it's difficult to move efficiently and accurately.</li>
    <li>So if you aren't highly experienced in seating and positioning, be sure to collaborate with someone who is. Involve your seating provider in your access evals, especially for wheelchair users. Building a collaborative relationship will generally make evals more fun and effective.</li>
    <li>Things can and do change. Ask your clients to keep you in the loop, to let you know if anything changes in their seating. And vice versa, when you work on access, loop in the seating provider.</li>
  </ul>
</div>
`;

const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.array, //[she, her, hers]
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their']
};

//content sub-component with unique content for this page
function DoAnEvalHomeContent(props) {
  const firstName = props.firstName;
  const pronouns = props.pronouns;
  return(
    <>
    <p>We'll guide you through a systematic process to help you achieve early success and build from there.  
    And hopefully have some fun along the way!</p>
    <p>Here's the basic sequence we'll cover:</p>
    <ul>
      <li>Gather background info about {firstName} and {pronouns[2]} situation</li>
      <li>Evaluate {firstName}'s abilities</li>
      <li>Identify and test-drive potential access solutions</li>
      <li>Review results and determine next steps</li>
    </ul>
    <p>Click <strong>Continue</strong> to begin the Background info section.</p>
    </>
  );
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function DoAnEvalHome(props) {
  const firstName = props.firstName;
  const pronouns = props.pronouns;
  const pageData = {
    image: "./assets/images/target-icon.jpg", //not yet used, but could supply an image on page
    faIcon: "fa-universal-access", //include this font-awesome icon in the image column of the page
    title: "OK, let's get started!",
    subTitle: "",
    learnMore: {linkText: "Learn More about our approach to access assessments",
                title: "Our Approach to Access Assessments",
                content: DO_EVAL_HOME_HELP
                },
    nextRoute: TABS.GATHER, //SUBTABS.GOALS, //static name for next page since this never changes
    sidebar: false
  }

  console.log("rendering DoAnEvalHome, firstName= : " + firstName);

  return(
    <>
    <Body page={pageData} contentComponent={<DoAnEvalHomeContent firstName={firstName} pronouns={pronouns}/>} />
    </>
  );
}

DoAnEvalHome.propTypes = propTypes;
DoAnEvalHome.defaultProps = defaultProps;

export default DoAnEvalHome;